<template>
  <ModalContainer :show="show">
    <template slot="body">
      <div class="modal-content">
        <div class="d-flex align-items-center justify-content-between">
          <span class="ml-1 font-18 font-bold">{{ $t("export") }}</span>
          <img
            src="../../assets/images/Vector (1).png"
            alt="close"
            class="cursor-pointer mt-1 mr-1"
            @click="$emit('close')"
          />
        </div>

        <div
          class="d-flex flex-column align-items-center justify-contents-center p-10"
        >
          <div class="w-100 mt-4">
            <!-- <label for="timezone">{{ timezone }}</label> -->
            <div
              class="input d-flex align-items-center justify-content-between p-15 cursor-pointer"
              @click="show_globe = !show_globe"
              id="timezone"
            >
              <span v-if="!currentTimezone">{{ $t("select_timezone") }}</span>
              <span v-else>{{ currentTimezone }}</span>
              <icon-dropdown />
            </div>

            <div
              class="dropdown-lang position-absolute p-20 pt-3 w-100"
              v-if="show_globe"
              v-click-outside="hideGlobe"
            >
              <table-search
                containerClass="w-100 mb-2"
                @handleSearch="
                  ($event) => (searchTimezone = $event.target.value)
                "
                :searchQuery="searchTimezone"
              />

              <div class="timezone-container">
                <div
                  v-for="timezone in filteredTimezones"
                  :key="timezone"
                  class="timezone"
                  @click="timezoneClicked"
                  :class="{ 'active-zone': timezone === currentTimezone }"
                >
                  {{ timezone }}
                </div>
              </div>
            </div>
          </div>
          <select
            class="w-100 mt-4 form-control"
            :value="downloadTo"
            @input="handleDownloadToChange($event.target.value)"
          >
            <option value="" disabled>Select Option</option>
            <option value="email">Send Via Email</option>
            <option value="computer">Download To Your Computer</option>
          </select>

          <!-- Format Options -->
          <div v-if="downloadTo" class="w-100 mt-4">
            <label class="mb-3 d-block font-weight-bold">{{
              $t("export_format") || "Export Format"
            }}</label>

            <!-- Radio buttons for computer (single selection) -->
            <div v-if="downloadTo === 'computer'" class="format-options">
              <div
                v-for="format in formats"
                :key="format.value"
                class="format-item"
              >
                <input
                  type="radio"
                  :id="`format-${format.value}`"
                  :value="format.value"
                  :checked="selectedExportFormat[0] === format.value"
                  @change="handleFormatChange(format.value)"
                />
                <label :for="`format-${format.value}`" class="format-label">
                  {{ format.label }}
                </label>
              </div>
            </div>

            <!-- Checkboxes for email (multiple selection) -->
            <div v-if="downloadTo === 'email'" class="format-options">
              <div
                v-for="format in formats"
                :key="format.value"
                class="format-item"
              >
                <input
                  type="checkbox"
                  :id="`format-${format.value}`"
                  :value="format.value"
                  :checked="selectedExportFormat.includes(format.value)"
                  @change="handleFormatToggle(format.value)"
                />
                <label :for="`format-${format.value}`" class="format-label">
                  {{ format.label }}
                </label>
              </div>
            </div>
          </div>

          <div
            class="btn-mt d-flex justify-contents-end align-items-center col-gap-2 ml-auto"
          >
            <button
              type="button"
              class="btn btn-primary w-100"
              @click="$emit('openExport')"
              :disabled="!currentTimezone || !isFormatSelected"
            >
              {{ $t("export") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalContainer>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import IconDropdown from "../../components/Iconsvg/IconDropdown.vue";
import ModalContainer from "../../components/ModalContainer";
import TableSearch from "../../components/TableSearch.vue";
import vClickOutside from "v-click-outside";

export default {
  name: "Timezone",
  components: {
    ModalContainer,
    IconDropdown,
    TableSearch,
  },
  props: {
    show: Boolean,
    downloadTo: String,
    selectedExportFormat: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      show_globe: false,
      searchTimezone: "",
      timezones: [],
      formats: [
        { label: "Export as PDF", value: "pdf" },
        { label: "Export as CSV", value: "csv" },
        { label: "Export as ASCII", value: "ascii" },
        { label: "Export as HTML", value: "html" },
        { label: "Export as JPEG", value: "jpeg" },
        { label: "Export as PDF-Log", value: "pdf-log" },
      ],
    };
  },
  computed: {
    ...mapGetters(["reportTimezones", "currentTimezone"]),
    filteredTimezones() {
      return this.reportTimezones.filter((timezone) =>
        timezone.toLowerCase().includes(this.searchTimezone.toLowerCase())
      );
    },
    isFormatSelected() {
      if (!this.downloadTo) return false;
      if (this.downloadTo === "computer") {
        return this.selectedExportFormat.length === 1;
      } else if (this.downloadTo === "email") {
        return this.selectedExportFormat.length > 0;
      }
      return false;
    },
  },
  methods: {
    ...mapActions(["setTimezone"]),
    hideGlobe() {
      this.show_globe = false;
    },
    timezoneClicked(event) {
      this.setTimezone(event.target.innerText);
      this.show_globe = false;
    },
    handleDownloadToChange(value) {
      this.$emit("update:downloadTo", value);
      // Reset format selection when downloadTo changes to avoid confusion
      this.$emit("update:selectedExportFormat", []);
    },
    handleFormatChange(formatValue) {
      // For radio buttons (computer) - single selection
      this.$emit("update:selectedExportFormat", [formatValue]);
    },
    handleFormatToggle(formatValue) {
      // For checkboxes (email) - multiple selection
      const currentFormats = [...this.selectedExportFormat];
      const index = currentFormats.indexOf(formatValue);

      if (index > -1) {
        // Remove if already selected
        currentFormats.splice(index, 1);
      } else {
        // Add if not selected
        currentFormats.push(formatValue);
      }

      this.$emit("update:selectedExportFormat", currentFormats);
    },
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
};
</script>

<style scoped>
.modal-content {
  width: 100%;
}
.body {
  padding: 50px;
}
.input {
  background: rgba(240, 244, 251, 0.6) !important;
}
.timezone {
  padding: 10px 5px;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
}

.active-zone {
  background: #dee2eb;
  color: #213e7a;
}
.timezone-container {
  height: 17rem;
  overflow-y: scroll;
}
.search-timezone {
  background: white;
}
.active-zone {
  background: #dee2eb;
  color: #213e7a;
}
.dropdown-lang {
  background: #ffffff;
  /* border: 0.3px solid rgba(99, 99, 99, 0.25); */
  box-shadow: 0px 20px 50px rgba(29, 53, 71, 0.06);
  border-radius: 3px;
  z-index: 3 !important;
  top: 8rem;
  color: #5e6a79;
  /* width: calc(100% - 1.5rem); */
  width: max-content;
}

.btn-mt {
  margin-top: 2rem;
}

.format-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.format-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.format-item input[type="radio"],
.format-item input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.format-label {
  cursor: pointer;
  margin: 0;
  font-size: 14px;
  color: #5e6a79;
}

@media (max-width: 575.98px) {
  .modal-content {
    width: 100% !important;
  }
}
</style>
